import type { SessionProjectionEnvelopeV1 } from './session-projection.js'

export type ClientSessionStoreListener = () => void

/**
 * Browser-side view cache for server-committed session projections.
 *
 * This store deliberately has no persistence or optimistic mutation API. A
 * caller may only replace the snapshot with a newer envelope committed for
 * the expected session. Optimistic UI state belongs in a separate UI store.
 */
export class ClientSessionStore {
  private snapshot: SessionProjectionEnvelopeV1 | null = null
  /** Last envelope applied with a full accept (transcript rebuild). */
  private highestAcceptedVersion: number | null = null
  /**
   * Last voice-facts envelope. This does not move highestAcceptedVersion, so a
   * chat or /api/pdf response at the same version can still rebuild the view.
   */
  private highestFactsVersion: number | null = null
  private readonly listeners = new Set<ClientSessionStoreListener>()

  constructor(private readonly expectedSessionId: string) {}

  getSnapshot = (): SessionProjectionEnvelopeV1 | null => this.snapshot

  subscribe = (listener: ClientSessionStoreListener): (() => void) => {
    this.listeners.add(listener)

    return () => {
      this.listeners.delete(listener)
    }
  }

  acceptServerEnvelope(envelope: SessionProjectionEnvelopeV1): boolean {
    if (envelope.sessionId !== this.expectedSessionId) {
      return false
    }

    if (
      this.highestAcceptedVersion !== null &&
      envelope.version <= this.highestAcceptedVersion
    ) {
      return false
    }
    // Voice may already hold this version's facts. An older full envelope would
    // wipe them. The same version is still applied, so the brief can rebuild.
    if (
      this.highestFactsVersion !== null &&
      envelope.version < this.highestFactsVersion
    ) {
      return false
    }

    this.snapshot = envelope
    this.highestAcceptedVersion = envelope.version
    if (this.highestFactsVersion === null || envelope.version > this.highestFactsVersion) {
      this.highestFactsVersion = envelope.version
    }
    this.notify()
    return true
  }

  /**
   * Store the projection for the tracker only. The full-accept watermark stays
   * where the last transcript rebuild left it.
   */
  acceptFactsEnvelope(envelope: SessionProjectionEnvelopeV1): boolean {
    if (envelope.sessionId !== this.expectedSessionId) {
      return false
    }
    if (
      this.highestFactsVersion !== null &&
      envelope.version <= this.highestFactsVersion
    ) {
      return false
    }
    if (
      this.highestAcceptedVersion !== null &&
      envelope.version <= this.highestAcceptedVersion
    ) {
      return false
    }

    this.snapshot = envelope
    this.highestFactsVersion = envelope.version
    this.notify()
    return true
  }

  clear(): void {
    if (!this.snapshot) {
      return
    }

    if (
      this.highestFactsVersion !== null &&
      (this.highestAcceptedVersion === null || this.highestFactsVersion > this.highestAcceptedVersion)
    ) {
      this.highestAcceptedVersion = this.highestFactsVersion
    }
    this.snapshot = null
    this.notify()
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener()
    }
  }
}
